<template>
  <router-link :to="`/books/${book.id}`" class="block group">
    <article class="overflow-hidden rounded-xl border border-ink-100 bg-white shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-jackfruit-300 hover:shadow-fruit">
      <div class="relative aspect-[3/4] overflow-hidden" :class="coverBgClass">
        <img
          v-if="coverImageUrl"
          :src="coverImageUrl"
          :alt="book.title"
          class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          @error="onCoverError"
        />
        <div v-else class="absolute inset-0 flex items-center justify-center">
          <div class="text-center">
            <div class="text-5xl transition-transform duration-300 group-hover:scale-110">📖</div>
            <div class="mt-2 text-xs font-semibold text-ink-500">菠萝蜜书单</div>
          </div>
        </div>
        
        <div class="absolute left-1.5 top-1.5 z-10 flex items-center gap-1 sm:left-2 sm:top-2 sm:gap-1.5">
          <span
            class="rounded-md px-1.5 py-0.5 text-[9px] font-bold text-white shadow-sm ring-1 ring-white/45 backdrop-blur-md sm:rounded-lg sm:px-2.5 sm:py-1 sm:text-[11px]"
            :style="categoryBadgeStyle"
          >
            {{ categoryLabel }}
          </span>
          <span
            v-if="subcategoryLabel"
            class="rounded-md px-1.5 py-0.5 text-[9px] font-bold text-ink-900 shadow-sm ring-1 ring-white/40 backdrop-blur-md sm:rounded-lg sm:px-2.5 sm:py-1 sm:text-[11px]"
            :style="subcategoryBadgeStyle"
          >
            {{ subcategoryLabel }}
          </span>
        </div>
        
        <div class="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/65 via-black/15 to-transparent p-1.5 sm:p-2.5">
          <span class="inline-flex items-center gap-1 rounded-md bg-white/25 px-1.5 py-0.5 text-[9px] font-bold text-white shadow-sm ring-1 ring-white/30 backdrop-blur-md [text-shadow:0_1px_2px_rgba(0,0,0,0.55)] sm:rounded-lg sm:px-2 sm:py-1 sm:text-[11px]">
            🔥 {{ formatViewCount(book.viewCount) }}追读
          </span>
        </div>
      </div>
      
      <div class="p-2 sm:p-3.5">
        <h3 class="line-clamp-1 text-xs font-extrabold text-ink-900 transition-colors group-hover:text-pulp-600 sm:text-sm">
          {{ book.title }}
        </h3>
        <div class="mt-1 flex items-center justify-between gap-1 sm:mt-1.5 sm:gap-2">
          <p class="truncate text-[10px] text-ink-500 sm:text-xs">{{ book.author }}</p>
          <p class="shrink-0 text-[10px] font-semibold text-ink-500 sm:text-xs">{{ book.totalChapters }}章</p>
        </div>
      </div>
    </article>
  </router-link>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Book } from '@/types/user'
import { SUBCATEGORIES } from '@/config/categories'

interface Props {
  book: Book
}

const props = defineProps<Props>()

// 封面图片加载失败时回退到占位
const coverFailed = ref(false)

const coverImageUrl = computed(() => {
  if (!props.book.coverUrl || coverFailed.value) return ''
  // 如果是完整URL直接使用
  if (props.book.coverUrl.startsWith('http')) return props.book.coverUrl
  // 相对路径（如 /uploads/covers/xxx.webp），走 Vite 代理到后端
  return props.book.coverUrl
})

const onCoverError = () => {
  coverFailed.value = true
}

const categoryLabel = computed(() => {
  const labels: Record<string, string> = {
    male: '男频',
    female: '女频',
    general: '综合',
    cyberpunk: '赛博'
  }
  return labels[props.book.category] || props.book.category
})

// 从 tags 中反查二级类目中文名
const subcategoryLabel = computed(() => {
  const tags = normalizedTags.value
  for (const sub of SUBCATEGORIES) {
    if (tags.includes(sub.subcategoryTag) || tags.includes(sub.id) || tags.includes(sub.label)) {
      return sub.label
    }
  }
  return ''
})

const coverBgClass = computed(() => {
  const map: Record<string, string> = {
    male: 'bg-gradient-to-br from-sky-100 to-blue-50',
    female: 'bg-gradient-to-br from-rose-100 to-pink-50',
    general: 'bg-gradient-to-br from-amber-100 to-orange-50',
    cyberpunk: 'bg-gradient-to-br from-cyan-100 to-violet-50'
  }
  return map[props.book.category] || 'bg-gradient-to-br from-gray-100 to-gray-50'
})

const categoryBadgeStyle = computed(() => {
  const map: Record<string, string> = {
    male: 'rgba(59, 130, 246, 0.68)',
    female: 'rgba(244, 63, 94, 0.68)',
    general: 'rgba(249, 115, 22, 0.68)',
    cyberpunk: 'rgba(14, 165, 233, 0.72)'
  }
  return { backgroundColor: map[props.book.category] || 'rgba(107, 114, 128, 0.68)' }
})

const subcategoryBadgeStyle = computed(() => {
  return { backgroundColor: 'rgba(254, 240, 138, 0.72)' }
})

const normalizedTags = computed(() => {
  const raw = props.book.tags
  if (Array.isArray(raw)) return raw
  if (typeof raw !== 'string') return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return raw.split(',').map(tag => tag.trim()).filter(Boolean)
  }
})

const formatViewCount = (count: number) => {
  if (!count) return '0'
  if (count >= 10000) return (count / 10000).toFixed(1) + '万'
  return count.toString()
}
</script>

<style scoped>
.line-clamp-1 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}
</style>
